import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
export const RoleGroup = {
    Legal: "legal",
    Business: "business"
};
export type RoleGroupType = typeof RoleGroup[keyof typeof RoleGroup];
class Role extends Base implements EntityType {
    roleId: uuid;
    name: string;
    active: boolean;
    users: uuid[];
    groups: uuid[];
    privileges: uuid[];
    grants?: {} | null;
    roleGroup?: RoleGroupType | null;
    constructor(doc: typeof Role | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            roleId: genUUID(),
            name: null,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "role";
        // @ts-ignore
        this.roleId = doc._roleId || doc.roleId || doc.roleid || genUUID();
        // @ts-ignore
        this.name = doc._name || doc.name || null;
        // @ts-ignore
        this.active = util.defaultBoolean([doc.active, doc._active, doc.active, true]);
        // @ts-ignore
        this.users = doc._users || doc.users || [];
        // @ts-ignore
        this.groups = doc._groups || doc.groups || [];
        // @ts-ignore
        this.privileges = doc._privileges || doc.privileges || [];
        // @ts-ignore
        this.grants = doc._grants || doc.grants || {};
        // @ts-ignore
        this.roleGroup = doc._roleGroup || doc.roleGroup || doc.rolegroup || null;
    }
    get id(): uuid { return this.roleId; }
    addUser(user: uuid): uuid {
        if (!user) throw new ValidationError('No nulls allowed in an array!');
        if (!this.users) this.users = [];
        this.users.push(user);
        return user;
    }
    updateUser(user: uuid) {
        if (!this.users) this.users = [];
        const index = this.users.findIndex(item => item === user);
        if (index !== -1) { this.users[index] = user; }
        else this.addUser(user);
    }
    removeUser(user: uuid) {
        if (!this.users) return;
        let foundItems = this.users?.filter(item => item === user);
        if (foundItems && foundItems.length > 0) {
            this.users = this.users.filter(item => !(item === user));
        }
    }
    abandonUsers() { this.users = []; }
    addGroup(group: uuid): uuid {
        if (!group) throw new ValidationError('No nulls allowed in an array!');
        if (!this.groups) this.groups = [];
        this.groups.push(group);
        return group;
    }
    updateGroup(group: uuid) {
        if (!this.groups) this.groups = [];
        const index = this.groups.findIndex(item => item === group);
        if (index !== -1) { this.groups[index] = group; }
        else this.addGroup(group);
    }
    removeGroup(group: uuid) {
        if (!this.groups) return;
        let foundItems = this.groups?.filter(item => item === group);
        if (foundItems && foundItems.length > 0) {
            this.groups = this.groups.filter(item => !(item === group));
        }
    }
    abandonGroups() { this.groups = []; }
    addPrivilege(privilege: uuid): uuid {
        if (!privilege) throw new ValidationError('No nulls allowed in an array!');
        if (!this.privileges) this.privileges = [];
        this.privileges.push(privilege);
        return privilege;
    }
    updatePrivilege(privilege: uuid) {
        if (!this.privileges) this.privileges = [];
        const index = this.privileges.findIndex(item => item === privilege);
        if (index !== -1) { this.privileges[index] = privilege; }
        else this.addPrivilege(privilege);
    }
    removePrivilege(privilege: uuid) {
        if (!this.privileges) return;
        let foundItems = this.privileges?.filter(item => item === privilege);
        if (foundItems && foundItems.length > 0) {
            this.privileges = this.privileges.filter(item => !(item === privilege));
        }
    }
    abandonPrivileges() { this.privileges = []; }
    static ROLE_GROUP_VALUES = ["legal", "business"];
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique ID for a role"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","comment":"Associated firm"}]; }
    toLowerCase() { return this.roleId; }
    static override getNonRefArrayFields(): string[] { return ["users", "groups", "privileges"]; }
    static override getDefaultEntries(): Role[] {
        return [{"roleId":"4a850be6-151c-4464-9388-c0930d6c2de9","name":"Administrator","status":"C"},{"roleId":"5a9d6f36-89b0-475a-b63c-0a46a5756d91","name":"Bookkeeper","status":"C"},{"roleId":"6b45522c-57db-46b1-8721-6dd682e97e3a","name":"Marketing Manager","status":"C"},{"roleId":"5dbf67ba-c5e7-4f53-97ee-d32d3f006c6f","name":"Office Manager","status":"C"},{"roleId":"3ee79b8a-a39e-49f0-b464-662ffbe0fc6b","name":"Paralegal","status":"C"},{"roleId":"4b78516a-a3fa-4ef5-9c6f-61bd29f3df4e","name":"Receptionist","status":"C"},{"roleId":"0def27f2-9004-4edc-948c-f9ba159d5659","name":"Salesperson","status":"C"},{"roleId":"958aa2d1-4064-4333-a5dd-a0f55c6b5b12","name":"Legal Secretary","status":"C"},{"roleId":"49609a1c-f35d-445a-a587-68c3c1dce618","name":"Human Resources","status":"C"}].map(e => new Role(e));
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'role',
    dataArea: 'Staff',
    table: 'ROLES',
    comment: 'A role is a named set of privileges assigned to users and/or groups to control access within the application. Predefined roles correspond to firm staff positions (e.g., Administrator, Paralegal, Bookkeeper, Receptionist) and govern which areas of the interface each staff member can use.',
    fields: {
        roleId: {
            type: 'uuid', required: true, pk: true, default: '__UUID__', comment: 'Unique ID for a role'
        },
        name: {
            type: 'string', required: true, comment: 'Name of the role (unique)'
        },
        active: {
            type: 'boolean', default: true, comment: 'Is the role active? Roles no longer used can be marked as inactive'
        },
        users: {
            type: 'uuid[]', entityType: 'user', comment: 'User IDs associated with this role'
        },
        groups: {
            type: 'uuid[]', entityType: 'group', comment: 'Group IDs associated with this role'
        },
        privileges: {
            type: 'uuid[]', entityType: 'privilege', comment: 'Privilege IDs associated with this role'
        },
        grants: {
            type: 'jsonb',
            keptWhenOmitted: true,
            comment: 'What the Role grants: {areas: {<Data Area>: {read, write, delete, aggregate: none, own or all}}, actions: [<Action Privilege>]}. A Role with no value grants nothing.'
        },
        roleGroup: {
            type: 'string',
            values: ['legal', 'business'],
            keptWhenOmitted: true,
            comment: 'The Role Group: legal is Legal Staff, business is Business Staff. A Role with no value is Business Staff.'
        }
    },
    // TODO: These should be added on the server, as it is imperative that they exist when creating the first user!
    entries: [
        {roleId: '4a850be6-151c-4464-9388-c0930d6c2de9', name: 'Administrator'},
        // {roleId: '8d8f87c7-a8b9-4028-a226-0774b374a6a6', name: 'Attorney'}, // Do not do attorney, as we created that role already!
        {roleId: '5a9d6f36-89b0-475a-b63c-0a46a5756d91', name: 'Bookkeeper'},
        {roleId: '6b45522c-57db-46b1-8721-6dd682e97e3a', name: 'Marketing Manager'},
        {roleId: '5dbf67ba-c5e7-4f53-97ee-d32d3f006c6f', name: 'Office Manager'},
        // {roleId: '2146272e-ec31-4a87-924f-2aa3a372fd7d', name: 'Owner'}, // Do not do owner, as we created that role already!
        {roleId: '3ee79b8a-a39e-49f0-b464-662ffbe0fc6b', name: 'Paralegal'},
        {roleId: '4b78516a-a3fa-4ef5-9c6f-61bd29f3df4e', name: 'Receptionist'},
        {roleId: '0def27f2-9004-4edc-948c-f9ba159d5659', name: 'Salesperson'},
        {roleId: '958aa2d1-4064-4333-a5dd-a0f55c6b5b12', name: 'Legal Secretary'},
        {roleId: '49609a1c-f35d-445a-a587-68c3c1dce618', name: 'Human Resources'},
    ],
    maps: [
        {name: 'users', keysFn: t => t.users, presentFn: (t, k) => t.users?.includes(k)},
        {name: 'groups', keysFn: t => t.groups, presentFn: (t, k) => t.groups?.includes(k)},
        {name: 'privileges', keysFn: t => t.privileges, presentFn: (t, k) => t.privileges?.includes(k)}
    ]
});
    }
}
export default Role;
